import { Tab, Tabs } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { compose, mapProps, withProps } from 'recompose';
import ObjectInput from '@/se/components/inputs/ObjectInput';

import TextInput from '@/se/components/inputs/TextInput';
import AddressInput from '../AddressInput';
import PhoneInput from '@/se/components/inputs/PhoneInput';
import MonitoringAssignment from '../../entities/hospitals/MonitoringAssignment';
import GroupAdminAssignment from '../../entities/hospitals/GroupAdminAssignment';
import { withLabel } from '@/se/components/Label';
import NotesInput from '../NotesInput';
import NumberInput from '@/se/components/inputs/NumberInput';
import ToggleInput from '@/se/components/inputs/ToogleInput';
import graphqlSelectInput from '../graphqlSelectInput';
import { externalScheduleProviders, timezoneOptions } from '@/graph/hospitals';
import get from 'lodash/get';
import SchedulePdfsMultipleSelectInput from '../SchedulePdfsMultipleSelectInput';
import { withDescription } from '@/se/components/Description';
import AnalyticsPdfsMultipleSelectInput from '../AnalyticsPdfsMultipleSelectInput';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    height: '100%',
    minWidth: 800,
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
      flexDirection: 'column',
    },
  },
  tabs: {
    borderRight: `1px solid ${theme.palette.divider}`,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      flex: 'auto',
      flexDirection: 'column',
    },
  },
}));

const VerticalTabs = ({ tabs }) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const [orientation, setOrientation] = React.useState(
    typeof window !== 'undefined' && window.innerWidth <= 960 ? 'horizontal' : 'vertical'
  );

  React.useEffect(() => {
    const handleResize = () => {
      setOrientation(window.innerWidth <= 960 ? 'horizontal' : 'vertical');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <div className={classes.root}>
      <Tabs
        orientation={orientation}
        variant="scrollable"
        value={value}
        onChange={handleChange}
        aria-label="Edit Hospital"
        className={classes.tabs}
        indicatorColor="primary"
      >
        {tabs.map((tab, idx) => (
          <Tab key={idx} label={tab.label} {...a11yProps(idx)} />
        ))}
      </Tabs>
      {tabs.map((tab, idx) => (
        <TabPanel key={idx} value={value} index={idx} style={{ flex: 3 }}>
          {tab.content}
        </TabPanel>
      ))}
    </div>
  );
};

const PhoneNumberInput = compose(
  withLabel('Contact Phone Number'),
  withProps({ placeholder: 'e.g., (619) 412-9512' })
)(PhoneInput);

const Span = styled.span`
  display: flex;
  width: 100%;
  align-items: center;
  > :first-child {
    flex: 1;
  }
  * + * {
    margin: 0;
    padding-left: 1rem;
  }
`;

export const LinkInput = withProps({
  schema: {
    link: withProps({ placeholder: 'e.g., https://g.page/ucsd/review?mt', type: 'url' })(TextInput),
    enabled: ToggleInput,
  },
  render: ({ link, enabled }) => (
    <Span style={{ display: 'flex' }}>
      {link}
      {enabled}
    </Span>
  ),
})(ObjectInput);

const ScheduleProviderInput = withProps({
  isClearable: true,
})(
  graphqlSelectInput({
    entityName: 'externalScheduleProvider',
    label: 'External Schedule CSV Provider',
    placeholder: 'Select',
    graphqlList: externalScheduleProviders,
    transformOptions: options => options.map(opt => ({ id: opt, name: opt })),
  })
);

// Label displayed when generic parser (CSV or HL7) is configured
const GenericParserLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 0;

  .label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .value {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1rem;
  }

  .badge {
    background: rgba(0, 200, 150, 0.2);
    color: rgb(0, 200, 150);
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
  }
`;

// Read-only display for generic CSV parser config
const GenericCsvParserDisplay = ({ configValue }) => (
  <GenericParserLabel>
    <span className="label">External Schedule CSV Provider</span>
    <div className="value">
      <span>{configValue}</span>
      <span className="badge">Generic CSV Parser</span>
    </div>
  </GenericParserLabel>
);

// Read-only display for generic HL7 parser config
const GenericHl7ParserDisplay = ({ configValue }) => (
  <GenericParserLabel>
    <span className="label">HL7 Schedule Provider</span>
    <div className="value">
      <span>{configValue}</span>
      <span className="badge">Generic HL7 Parser</span>
    </div>
  </GenericParserLabel>
);

export const TimezoneInput = graphqlSelectInput({
  entityName: 'timezone',
  label: 'Timezone',
  placeholder: 'Select',
  graphqlList: timezoneOptions,
  getOptionValue: option => (option ? option.value : undefined),
  transformOptions: options => options.map(({ id, name }) => ({ id, name })),
});

export default withProps(props => {
  const genericCsvConfig = get(props, 'value.genericCsvParserConfig');
  const genericHl7Config = get(props, 'value.genericHl7ParserConfig');

  return {
    schema: {
      name: withProps({ placeholder: 'e.g. Surgical Care Partners', required: true })(withLabel('Name')(TextInput)),
      address: AddressInput,
      contact: PhoneNumberInput,
      notes: NotesInput,
      reviewIntegrations: compose(
        withLabel('Patient Review Integrations', { section: true }),
        withProps({
          schema: {
            google: withLabel('Google')(LinkInput),
            yelp: withLabel('Yelp!')(LinkInput),
          },
          render: ({ google, yelp }) => (
            <div>
              {google}
              {yelp}
            </div>
          ),
        })
      )(ObjectInput),
      caretakerReviewIntegrations: compose(
        withLabel('Caretaker Review Integrations', { section: true }),
        withProps({
          schema: {
            google: withLabel('Google')(LinkInput),
            yelp: withLabel('Yelp!')(LinkInput),
          },
          render: ({ google, yelp }) => (
            <div>
              {google}
              {yelp}
            </div>
          ),
        })
      )(ObjectInput),
      ospitekOrdersIntegrations: compose(
        withLabel('Ospitek Orders Integrations', { section: true }),
        withProps({
          schema: {
            externalLink: withLabel('External Link')(LinkInput),
          },
          render: ({ externalLink }) => <div>{externalLink}</div>,
        })
      )(ObjectInput),
      monitors: withLabel('Monitoring Engineers')(MonitoringAssignment),
      groupAdmins: withLabel('Group Admins')(GroupAdminAssignment),
      minBracelets: withProps({ min: 1 })(withLabel('Minimum Active Bracelets')(NumberInput)),
      questionnaireValidMonths: withProps({ min: 0, required: true })(
        withLabel('Questionnaire valid in months for repeated patients')(NumberInput)
      ),
      passwordRefreshDays: withProps({ min: 0 })(
        withLabel('Period in days after which user is forced to change password')(NumberInput)
      ),
      sessionTimeout: withProps({ min: 60 })(withLabel('Session Expiration Timeout (in seconds)')(NumberInput)),

      scheduleProvider: ScheduleProviderInput,
      timezone: mapProps(props => ({ ...props, value: get(props, 'value.id', get(props, 'value')) }))(TimezoneInput),
      modules: compose(
        withLabel('Hospital Module Use', { section: true }),
        withProps({
          schema: {
            temperatureReporting: withProps({ label: 'Temperature Sensors and Reporting' })(ToggleInput),
            patientRegistration: withProps({ label: 'Patient Enrollment' })(ToggleInput),
            blockSchedule: withProps({ label: 'Block Schedule' })(ToggleInput),
            legacyBlockSchedule: withProps({ label: 'Legacy Block Schedule' })(ToggleInput),
            airFiltrationTimer: withProps({ label: 'Air Filtration Warning' })(ToggleInput),
            automaticPatientTracking: withProps({ label: 'Automatic Patient Tracking' })(ToggleInput),
            lightMode: withProps({ label: 'Light Mode' })(ToggleInput),
            acceptsProcedureTransfer: withProps({ label: 'Accepts Procedure Transfers' })(ToggleInput),
            hasPostop: withProps({ label: 'Has Post OP' })(ToggleInput),
            hasPatientTypes: withProps({ label: 'Has Patient Types' })(ToggleInput),
            createProcedureSchedule: withProps({ label: 'Create Procedure On Schedule' })(ToggleInput),
            noteTablet: withProps({ label: 'Pre-Op and Pacu Note on Tablet' })(ToggleInput),
            procedureNoteTablet: withProps({ label: 'Procedure Pre-Op and Pacu Note on Tablet' })(ToggleInput),
            patientCharting: withProps({ label: 'Patient Chart' })(ToggleInput),
            preOpStatusReset: withProps({ label: 'Pre-Op Status Reset' })(ToggleInput),
            orTabletV2: withProps({ label: 'Use Operation Room Tablet v2 (turning off uses deprecated v1)' })(
              ToggleInput
            ),
            inTakeForms: withProps({ label: 'Enable Registration Package' })(ToggleInput),
            dictation: withProps({ label: 'Enable Dictation' })(ToggleInput),
            showQRCode: withProps({ label: 'Show QR Code on Forms' })(ToggleInput),
            analyticsV2: withProps({ label: 'Analytics V2' })(ToggleInput),
            analyticsV2PieCharts: withProps({ label: 'Analytics V2 Pie Charts' })(ToggleInput),
            pacuAnalytics: withProps({ label: 'PACU Analytics' })(ToggleInput),
            anesthesiologistUtilizationAnalytics: withProps({ label: 'Anesthesiologist Utilization Analytics' })(
              ToggleInput
            ),
            showStaffCost: withProps({ label: 'Staff Cost' })(ToggleInput),
            showStaffTracking: withProps({ label: 'Staff Tracking' })(ToggleInput),
            showPreOpNote: withProps({ label: 'Show PreOp Note on Forms' })(ToggleInput),
            showCSVUpload: withProps({ label: 'Upload Schdule CSV' })(ToggleInput),
            showWeekends: withProps({ label: 'Show Weekends on Weekly/Monthly Schedule' })(ToggleInput),
            secondaryRoomAnesthesiologist: withProps({ label: 'Enable Secondary Room Anesthesiologist' })(ToggleInput),
            autoDischarge: withProps({ label: 'Auto Discharge Before 24h In' })(ToggleInput),
            preOpPriority: compose(
              withLabel('PreOp Priority', { section: true }),
              withProps({
                schema: {
                  hasPreOpPriority: withProps({ label: 'Has PreOp Priority' })(ToggleInput),
                  priorityCounter: withProps({ min: 0 })(withLabel('Priority Counter')(NumberInput)),
                },
                render: ({ hasPreOpPriority, priorityCounter }) => (
                  <div>
                    {hasPreOpPriority}
                    {hasPreOpPriority && priorityCounter}
                  </div>
                ),
                initialValue: {
                  hasPreOpPriority: false,
                  priorityCounter: 0,
                },
              })
            )(ObjectInput),
            schedulePDFs: withLabel('Select Schedule PDFs')(SchedulePdfsMultipleSelectInput),
            analyticsPDFs: withLabel('Select Analytics PDFs')(AnalyticsPdfsMultipleSelectInput),
            chartingIPAddressWhitelistEnabled: withProps({ label: 'Charting IP Address Whitelist' })(ToggleInput),
            chartingIPAddressWhitelist: compose(
              withLabel('Charting IP Addresses Whitelist'),
              withDescription('To add multiple IP addresses, separate them with a comma.')
            )(TextInput),
            ipAddressWhitelistEnabled: withProps({ label: 'IP Address Whitelist' })(ToggleInput),
            ipAddressWhitelist: compose(
              withLabel('IP Addresses Whitelist'),
              withDescription('To add multiple IP addresses, separate them with a comma.')
            )(TextInput),
            hl7UpdateDoesNotUpdateRoom: withProps({ label: 'HL7 update does not update room' })(ToggleInput),
            autoPhysicianIn: withProps({ label: 'Auto Physician In' })(ToggleInput),
            autoProcedureRoomChange: withProps({ label: 'Auto Procedure Room Change' })(ToggleInput),
          },
          render: ({
            temperatureReporting,
            patientRegistration,
            blockSchedule,
            legacyBlockSchedule,
            airFiltrationTimer,
            automaticPatientTracking,
            lightMode,
            acceptsProcedureTransfer,
            hasPostop,
            hasPatientTypes,
            createProcedureSchedule,
            noteTablet,
            procedureNoteTablet,
            patientCharting,
            preOpStatusReset,
            orTabletV2,
            inTakeForms,
            dictation,
            showQRCode,
            analyticsV2,
            analyticsV2PieCharts,
            pacuAnalytics,
            anesthesiologistUtilizationAnalytics,
            showStaffCost,
            showStaffTracking,
            showPreOpNote,
            showCSVUpload,
            showWeekends,
            secondaryRoomAnesthesiologist,
            autoDischarge,
            preOpPriority,
            schedulePDFs,
            analyticsPDFs,
            chartingIPAddressWhitelistEnabled,
            chartingIPAddressWhitelist,
            ipAddressWhitelistEnabled,
            ipAddressWhitelist,
            hl7UpdateDoesNotUpdateRoom,
            autoPhysicianIn,
            autoProcedureRoomChange,
          }) => (
            <div>
              {temperatureReporting}
              {patientRegistration}
              {blockSchedule}
              {legacyBlockSchedule}
              {airFiltrationTimer}
              {automaticPatientTracking}
              {lightMode}
              {acceptsProcedureTransfer}
              {hasPostop}
              {hasPatientTypes}
              {createProcedureSchedule}
              {noteTablet}
              {procedureNoteTablet}
              {patientCharting}
              {preOpStatusReset}
              {orTabletV2}
              {inTakeForms}
              {dictation}
              {showQRCode}
              {analyticsV2}
              {analyticsV2PieCharts}
              {pacuAnalytics}
              {anesthesiologistUtilizationAnalytics}
              {showStaffCost}
              {showStaffTracking}
              {showPreOpNote}
              {showCSVUpload}
              {showWeekends}
              {secondaryRoomAnesthesiologist}
              {autoDischarge}
              {preOpPriority}
              {schedulePDFs}
              {analyticsPDFs}
              {chartingIPAddressWhitelistEnabled}
              {chartingIPAddressWhitelist}
              {ipAddressWhitelistEnabled}
              {ipAddressWhitelist}
              {hl7UpdateDoesNotUpdateRoom}
              {autoPhysicianIn}
              {autoProcedureRoomChange}
            </div>
          ),
          initialValue: {
            preOpPriority: {
              hasPreOpPriority: false,
              priorityCounter: 0,
            },
          },
        })
      )(ObjectInput),
    },
    render: props => (
      <VerticalTabs
        tabs={[
          {
            label: 'General',
            content: (
              <div>
                {props.name}
                {props.address}
                {props.contact}
                {props.notes}
              </div>
            ),
          },
          {
            label: 'Integrations',
            content: (
              <div>
                {props.reviewIntegrations}
                {props.caretakerReviewIntegrations}
                {props.ospitekOrdersIntegrations}
              </div>
            ),
          },
          { label: 'Monitoring', content: <div>{props.monitors}</div> },
          { label: 'Group Admins', content: <div>{props.groupAdmins}</div> },
          {
            label: 'Settings',
            content: (
              <div>
                {props.minBracelets}
                {props.questionnaireValidMonths}
                {props.passwordRefreshDays}
                {props.sessionTimeout}
                {genericCsvConfig ? <GenericCsvParserDisplay configValue={genericCsvConfig} /> : props.scheduleProvider}
                {genericHl7Config && <GenericHl7ParserDisplay configValue={genericHl7Config} />}
                {props.timezone}
                {props.modules}
              </div>
            ),
          },
          {
            label: 'Links',
            content: (
              <div>
                {props.link} {props.enabled}{' '}
              </div>
            ),
          },
          {
            label: 'Schedule Provider',
            content: (
              <div>
                {genericCsvConfig ? <GenericCsvParserDisplay configValue={genericCsvConfig} /> : props.scheduleProvider}
                {genericHl7Config && <GenericHl7ParserDisplay configValue={genericHl7Config} />}
                {props.timezone}
              </div>
            ),
          },
        ]}
      />
    ),
  };
})(ObjectInput);
